<template>
  <div class="core-page">
    <div class="core-page-header">
      <div class="core-page-header-main">
        <h4>Comparisons</h4>
      </div>
    </div>

    <div class="card comparisons-card">
      <div class="comparisons-tabs-wrapper">
        <ul ref="tabsEl" class="tabs comparisons-tabs">
          <li class="tab comparisons-tab">
            <a class="active" href="#weekly-actual-revenue">
              <i class="material-icons">payments</i>
              <span>Weekly Actual Revenue</span>
            </a>
          </li>

          <li class="tab comparisons-tab">
            <a href="#budget">
              <i class="material-icons">account_balance</i>
              <span>Budget</span>
            </a>
          </li>

          <li class="tab comparisons-tab">
            <a href="#run-rate">
              <i class="material-icons">trending_up</i>
              <span>Run Rate</span>
            </a>
          </li>

          <li class="tab comparisons-tab">
            <a href="#petty-cash-vs-revenue">
              <i class="material-icons">receipt_long</i>
              <span>Petty Cash vs Revenue</span>
            </a>
          </li>
        </ul>
      </div>

      <div class="comparisons-content">
        <div id="weekly-actual-revenue" class="comparisons-tab-content">
          <WeeklyActualRevenueContent v-if="loadedTabs.weeklyActualRevenue" />
        </div>

        <div id="budget" class="comparisons-tab-content">
          <BudgetWeekPayloadContent v-if="loadedTabs.budget" />
        </div>

        <div id="run-rate" class="comparisons-tab-content">
          <RunRateContent v-if="loadedTabs.runRate" />
        </div>

        <div id="petty-cash-vs-revenue" class="comparisons-tab-content">
          <PettyCashVsRevenueContent v-if="loadedTabs.pettyCashVsRevenue" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, reactive, ref } from "vue";

import WeeklyActualRevenueContent from "@/components/comparisons/WeeklyActualRevenueContent.vue";
import BudgetWeekPayloadContent from "@/components/comparisons/BudgetWeekPayloadContent.vue";
import RunRateContent from "@/components/comparisons/RunRateContent.vue";
import PettyCashVsRevenueContent from "@/components/comparisons/PettyCashVsRevenueContent.vue";

const tabsEl = ref(null);

const loadedTabs = reactive({
  weeklyActualRevenue: true,
  budget: false,
  runRate: false,
  pettyCashVsRevenue: false,
});

let tabsInstance = null;

function markTabLoaded(tabId) {
  switch (tabId) {
    case "weekly-actual-revenue":
      loadedTabs.weeklyActualRevenue = true;
      break;

    case "budget":
      loadedTabs.budget = true;
      break;

    case "run-rate":
      loadedTabs.runRate = true;
      break;

    case "petty-cash-vs-revenue":
      loadedTabs.pettyCashVsRevenue = true;
      break;

    default:
      break;
  }
}

function destroyTabs() {
  try {
    if (tabsInstance && typeof tabsInstance.destroy === "function") {
      tabsInstance.destroy();
    }
  } catch (error) {
    // Ignore Materialize cleanup errors.
  } finally {
    tabsInstance = null;
  }
}

async function initTabs() {
  await nextTick();

  destroyTabs();

  if (!tabsEl.value) {
    return;
  }

  // eslint-disable-next-line no-undef
  if (typeof M !== "undefined" && M.Tabs) {
    // eslint-disable-next-line no-undef
    tabsInstance = M.Tabs.init(tabsEl.value, {
      swipeable: false,

      onShow: (tabContent) => {
        markTabLoaded(tabContent?.id);
      },
    });
  }
}

onMounted(() => {
  initTabs();
});

onBeforeUnmount(() => {
  destroyTabs();
});
</script>

<style scoped>
.comparisons-card {
  padding: 0;
  overflow: hidden;
}

.comparisons-tabs-wrapper {
  overflow-x: auto;
  overflow-y: hidden;
  border-bottom: 1px solid #e0e0e0;
  -webkit-overflow-scrolling: touch;
}

.comparisons-tabs {
  display: flex;
  width: max-content;
  min-width: 100%;
  margin: 0;
}

.comparisons-tabs .comparisons-tab {
  flex: 0 0 auto;
  border-right: 1px solid #eeeeee;
  text-transform: none;
}

.comparisons-tabs .comparisons-tab a {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding-left: 24px;
  padding-right: 24px;
  color: var(--coredeck-primary);
  text-transform: none;
  white-space: nowrap;
}

.comparisons-tabs .comparisons-tab a .material-icons {
  margin: 0;
  font-size: 20px;
  line-height: 1;
  color: inherit;
}

.comparisons-tabs .comparisons-tab a.active {
  color: var(--coredeck-teal);
}

.comparisons-tabs .comparisons-tab a:hover {
  color: var(--coredeck-teal);
}

.comparisons-tabs :deep(.indicator) {
  background-color: var(--coredeck-teal);
}

.comparisons-tab-content {
  padding: var(--core-space-10);
}

@media only screen and (max-width: 600px) {
  .comparisons-tabs {
    min-width: max-content;
  }

  .comparisons-tabs .comparisons-tab a {
    padding-left: 16px;
    padding-right: 16px;
  }

  .comparisons-tab-content {
    padding: var(--core-space-7);
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}
</style>
